import { updateActiveUser, updateRenewalToken, updateSessionUuid, updateToken } from '@redux/authent/authent.reducer';
import { isFirstConnectionExceptionPayload } from '@utils/common.utils';

import { AuthentSettings, DisplayModeEnum, User } from '@/types/authent/login.types';

import { AUTHENT_URL, authentApi } from './authent.api';

export const userApi = authentApi.injectEndpoints({
  endpoints: (builder) => ({
    getMe: builder.query<User, void>({
      query: () => ({
        url: `${AUTHENT_URL}/users/me`,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(updateActiveUser(data));
      },
      providesTags: (result, error) => {
        return [{ type: 'User', id: isFirstConnectionExceptionPayload(error) ? error.data.user_id : result?.id }];
      },
    }),
    checkPersonalPreviousPasswords: builder.mutation<boolean, { newPassword: string }>({
      query: ({ newPassword }) => ({
        url: `${AUTHENT_URL}/users/me/check-previous-passwords`,
        method: 'POST',
        body: newPassword,
      }),
    }),
    createUser: builder.mutation<User, Partial<User>>({
      query: (user) => ({
        url: `${AUTHENT_URL}/users`,
        method: 'POST',
        body: user,
      }),
    }),
    updateUser: builder.mutation<User, User>({
      query: (user) => ({
        url: `${AUTHENT_URL}/users/${user.id}`,
        method: 'PUT',
        body: user,
      }),
      invalidatesTags: (result) => [
        { type: 'User', id: result?.id },
        { type: 'User', id: 'LIST' },
      ],
    }),
    updateAuthentSettings: builder.mutation<AuthentSettings, AuthentSettings>({
      query: (configuration) => ({
        url: `${AUTHENT_URL}/settings`,
        method: 'PUT',
        body: configuration,
      }),
    }),
    updateDisplayMode: builder.mutation<
      { token: string | null; renew: string | null; sessionUuid: string | null },
      {
        displayMode: DisplayModeEnum;
      }
    >({
      query: (params: { displayMode: DisplayModeEnum }) => ({
        url: `${AUTHENT_URL}/settings/displayMode`,
        method: 'PUT',
        params: params,
      }),
      transformResponse: (_, meta) => {
        return {
          token: meta?.response?.headers.get('AUTHORIZATION') ?? null,
          renew: meta?.response?.headers.get('RENEWAL') ?? null,
          sessionUuid: meta?.response?.headers.get('Session-Uuid') ?? null,
        };
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(updateToken(data.token));
        dispatch(updateRenewalToken(data.renew));
        dispatch(updateSessionUuid(data.sessionUuid));
      },
    }),
    patchPersonalPassword: builder.mutation<User, { currentPassword: string; newPassword: string }>({
      query: ({ currentPassword, newPassword }) => ({
        url: `${AUTHENT_URL}/users/me/password`,
        method: 'PATCH',
        body: {
          currentPassword: currentPassword,
          newPassword: newPassword,
        },
      }),
      invalidatesTags: (result) => {
        if (result) {
          return [
            { type: 'User', id: result.id },
            { type: 'User', id: 'LIST' },
          ];
        }
        return [];
      },
    }),
    patchReinitializePassword: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `${AUTHENT_URL}/users/${id}/reinitialize-password`,
        method: 'PATCH',
      }),
    }),
    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `${AUTHENT_URL}/users/${id}`,
        method: 'DELETE',
      }),
    }),
    patchPersonalActiveGroup: builder.mutation<
      { token: string | null; renew: string | null; sessionUuid: string | null; user: User },
      { newActiveGroupId: number }
    >({
      query: ({ newActiveGroupId }) => ({
        url: `${AUTHENT_URL}/users/me/active-group`,
        method: 'PATCH',
        body: JSON.stringify(newActiveGroupId),
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: User, meta) => {
        return {
          token: meta?.response?.headers.get('AUTHORIZATION') ?? null,
          renew: meta?.response?.headers.get('RENEWAL') ?? null,
          sessionUuid: meta?.response?.headers.get('Session-Uuid') ?? null,
          user: response,
        };
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(updateToken(data.token));
        dispatch(updateRenewalToken(data.renew));
        dispatch(updateSessionUuid(data.sessionUuid));
      },
    }),
  }),
});

export const {
  useGetMeQuery,
  useUpdateUserMutation,
  useUpdateDisplayModeMutation,
  usePatchPersonalActiveGroupMutation,
  usePatchPersonalPasswordMutation,
  useCreateUserMutation,
  usePatchReinitializePasswordMutation,
  useDeleteUserMutation,
  useCheckPersonalPreviousPasswordsMutation,
} = userApi;
